import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  BarChart3,
  Building2,
  ClipboardList,
  History,
  KeyRound,
  LayoutDashboard,
  Layers,
  LogOut,
  Mail,
  MessageCircle,
  Plus,
  Search,
  Shield,
  Ticket,
  Timer,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CreateTicketDialog from "@/components/tickets/CreateTicketDialog";
import { ROLE_LABEL, ROLES, hasPermission } from "@/lib/constants";

const MAIN_NAV = [
  { id: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { id: "tickets", to: "/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets", permission: "view_tickets", roles: Object.values(ROLES).filter((role) => role !== ROLES.PR_ADMIN) },
  { id: "reports", to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: Object.values(ROLES).filter((role) => role !== ROLES.PR_ADMIN) },
  { id: "notifications", to: "/notifications", label: "Notifications", icon: MessageCircle, testid: "nav-notifications" },
];

const ADMIN_NAV = [
  {
    id: "admin_users",
    to: "/admin/users",
    permission: "manage_users",
    label: "Users",
    icon: Users,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.CLIENT_ADMIN],
    testid: "nav-admin-users",
  },
  {
    id: "admin_clients",
    to: "/admin/organizations",
    label: "Clients",
    icon: Building2,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN],
    testid: "nav-admin-organizations",
  },
  {
    id: "admin_applications",
    to: "/admin/applications",
    permission: "manage_applications",
    label: "Applications",
    icon: Layers,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-applications",
  },
  {
    id: "admin_sla",
    to: "/admin/sla",
    permission: "manage_sla",
    label: "SLA Policies",
    icon: Timer,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-sla",
  },
  {
    id: "admin_departments",
    to: "/admin/departments",
    label: "Departments",
    icon: Building2,
    roles: [ROLES.SUPER_ADMIN, ROLES.BUSINESS_ADMIN],
    testid: "nav-admin-departments",
  },
  {
    id: "admin_roles",
    to: "/admin/roles",
    label: "Roles & Permissions",
    icon: KeyRound,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-roles",
  },
  {
    id: "admin_smtp",
    to: "/admin/smtp",
    permission: "configure_smtp",
    label: "SMTP",
    icon: Mail,
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-smtp",
  },
  {
    id: "admin_whatsapp",
    to: "/admin/whatsapp",
    permission: "configure_whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-whatsapp",
  },
  {
    id: "admin_notification_logs",
    to: "/admin/notification-logs",
    permission: "view_notification_logs",
    label: "Notification Logs",
    icon: ClipboardList,
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-notiflogs",
  },
  {
    id: "admin_audit",
    to: "/admin/audit",
    permission: "view_audit",
    label: "Audit Log",
    icon: History,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-audit",
  },
];

const CREATE_ROLES = [
  ROLES.BUSINESS_USER,
  ROLES.CLIENT_ADMIN,
  ROLES.CLIENT_USER,
  ROLES.BUSINESS_ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.SUPPORT_ADMIN,
];

export default function Topbar() {
  const { user, logout } = useAuth();
  const [openCreate, setOpenCreate] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  const selectedNavigation = Array.isArray(user.navigation)
    ? new Set(user.navigation)
    : null;
  const allowed = (item) =>
    (!item.roles || item.roles.includes(user.role)) &&
    (!selectedNavigation || selectedNavigation.has(item.id)) &&
    (!item.permission || hasPermission(user, item.permission));
  const canCreate = CREATE_ROLES.includes(user.role) &&
    hasPermission(user, "create_tickets");
  const showCreateButton = canCreate &&
    (!selectedNavigation || selectedNavigation.has("new_ticket"));
  const adminItems = ADMIN_NAV.filter((item) => item.roles.includes(user.role) && allowed(item));
  const inAdmin = location.pathname.startsWith("/admin");
  const displayName = user.name || user.email;
  const initials = (displayName || "?").slice(0, 2).toUpperCase();

  const submitSearch = (event) => {
    event.preventDefault();
    const q = query.trim();
    navigate(q ? `/tickets?q=${encodeURIComponent(q)}` : "/tickets");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <div className="hd-top" data-testid="app-header">
        <header className="hd-header">
          <NavLink to="/dashboard" className="hd-company" data-testid="miledeep-logo-link">
            <div className="hd-logo" data-testid="miledeep-logo">MD</div>
            <div className="min-w-0">
              <div className="hd-company-name">Miledeep Help Desk</div>
              <div className="hd-powered">Powered by Quantira</div>
            </div>
          </NavLink>

          {hasPermission(user, "view_tickets") ? (
          <form className="hd-search" role="search" onSubmit={submitSearch}>
            <Search className="h-4 w-4" strokeWidth={1.8} />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search tickets by number, subject, customer..."
              aria-label="Search tickets"
              data-testid="global-search"
            />
          </form>
          ) : (
            <div className="flex-1" />
          )}

          <div className="hd-user-area">
            {showCreateButton && (
              <button
                type="button"
                data-testid="topbar-create-ticket"
                onClick={() => setOpenCreate(true)}
                className="hd-round-btn primary"
                aria-label="New Ticket"
              >
                <Plus className="h-4 w-4" strokeWidth={2} />
                <span className="hd-label">New Ticket</span>
              </button>
            )}

            <NavLink
              to="/profile"
              className="flex items-center gap-2.5"
              data-testid="topbar-profile"
              title="Profile"
            >
              <div className="hd-user-info" data-testid="sidebar-user-name">
                <strong>{displayName}</strong>
                <span>{ROLE_LABEL[user.role]}</span>
              </div>
              <div className="hd-avatar">{initials}</div>
            </NavLink>

            <button
              type="button"
              className="hd-logout"
              onClick={handleLogout}
              title="Sign out"
              aria-label="Sign out"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </header>

        <nav className="hd-main-menu" aria-label="Main navigation" data-testid="app-nav">
          {MAIN_NAV.filter(allowed).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) => `hd-main-btn ${isActive ? "active" : ""}`}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                <span>{item.id === "dashboard" && user.role === ROLES.PR_ADMIN ? "PR Dashboard" : item.label}</span>
              </NavLink>
            );
          })}

          {adminItems.length > 0 && (
            <NavLink
              to={adminItems[0].to}
              data-testid="nav-admin"
              className={`hd-main-btn ${inAdmin ? "active" : ""}`}
            >
              <Shield className="h-[18px] w-[18px]" strokeWidth={1.8} />
              <span>Administration</span>
            </NavLink>
          )}
        </nav>

        {inAdmin && adminItems.length > 0 && (
          <nav className="hd-second-menu" aria-label="Administration" data-testid="admin-nav">
            {adminItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) => `hd-pill-btn ${isActive ? "active" : ""}`}
                >
                  <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        )}
      </div>

      <CreateTicketDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={(ticket) => {
          window.dispatchEvent(new Event("helpdesk-ticket-changed"));
          navigate(`/tickets/${ticket.id}`);
        }}
      />
    </>
  );
}
